/**
 * Secure logger utility that provides structured logging with different log levels.
 * Logs can be conditionally enabled/disabled based on environment.
*/

import { tokenStorage } from '@/utils/tokenStorage'; // Import tokenStorage

// Define log levels
type LogLevel = "error" | "warn" | "info" | "debug";

// Define log data type
type LogData = Record<string, unknown>;

// Logger configuration options
interface LoggerConfig {
  minLevel: LogLevel;
  enableConsole: boolean;
  enablePersistence: boolean;
  maxLogSize?: number;
  enableRemoteLogging?: boolean;
  remoteLoggingEndpoint?: string;
  sensitiveKeys?: string[];
}

// Use VITE_API_URL for the base API path
const apiBaseUrl = import.meta.env.VITE_API_URL || ""; // Fallback to empty string if undefined

// Default configuration - production settings
const defaultConfig: LoggerConfig = {
  minLevel: process.env.NODE_ENV === "development" ? "debug" : "warn",
  enableConsole: process.env.NODE_ENV === "development",
  enablePersistence: true,
  maxLogSize: 100, // Maximum number of logs to keep in memory/storage
  enableRemoteLogging: process.env.NODE_ENV === "production",
  // Adjusted default endpoint path (relative to API base)
  remoteLoggingEndpoint: "/logs",
  sensitiveKeys: [
    "password",
    "token",
    "secret",
    "key",
    "auth",
    "credential",
    "authorization",
  ],
};

// Log level priority mapping - lower number means higher priority
const logLevelPriority: Record<LogLevel, number> = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3,
};

/**
 * Log entry type definition
 */
interface LogEntry {
  level: LogLevel;
  message: string;
  data?: LogData;
  timestamp: string;
  context?: string;
}

// Define log persistence strategy
interface LogPersistenceStrategy {
  saveLogs(logs: LogEntry[]): void;
  loadLogs(): LogEntry[];
  clearLogs(): void;
}

/**
 * Local storage strategy for log persistence
 */
class LocalStoragePersistence implements LogPersistenceStrategy {
  private readonly STORAGE_KEY = "app_logs";

  saveLogs(logs: LogEntry[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(logs));
    } catch (e) {
      console.error("Failed to persist logs to localStorage:", e);
    }
  }

  loadLogs(): LogEntry[] {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY) || "[]";
      return JSON.parse(data) as LogEntry[];
    } catch (e) {
      console.error("Failed to load logs from localStorage:", e);
      return [];
    }
  }

  clearLogs(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (e) {
      console.error("Failed to clear logs from localStorage:", e);
    }
  }
}

/**
 * Memory-only strategy for log persistence
 */
class MemoryPersistence implements LogPersistenceStrategy {
  private logs: LogEntry[] = [];

  saveLogs(logs: LogEntry[]): void {
    this.logs = logs;
  }

  loadLogs(): LogEntry[] {
    return this.logs;
  }

  clearLogs(): void {
    this.logs = [];
  }
}

/**
 * Main logger class with methods for different log levels
 */
class Logger {
  private config: LoggerConfig;
  private logHistory: LogEntry[] = [];
  private persistenceStrategy: LogPersistenceStrategy;
  private context: string = "";

  constructor(config: LoggerConfig = defaultConfig) {
    this.config = {
      ...defaultConfig,
      ...config,
    };

    // Select appropriate persistence strategy
    if (
      typeof window !== "undefined" &&
      window.localStorage &&
      this.config.enablePersistence
    ) {
      this.persistenceStrategy = new LocalStoragePersistence();
    } else {
      this.persistenceStrategy = new MemoryPersistence();
    }

    // Load any existing logs
    try {
      this.logHistory = this.persistenceStrategy.loadLogs();
    } catch {
      // Start with empty log history if loading fails
      this.logHistory = [];
    }

    // In development mode, log the logger initialization
    if (process.env.NODE_ENV === "development") {
      console.log("Logger initialized with config:", config);
    }
  }

  /**
   * Set a context identifier for subsequent logs
   */
  setContext(context: string): void {
    this.context = context;
  }

  /**
   * Clear the current context
   */
  clearContext(): void {
    this.context = "";
  }

  /**
   * Sanitize log data to remove sensitive information
   */
  private sanitizeData(data: unknown): unknown {
    if (!data || typeof data !== "object") return data;

    const sanitizedData = Array.isArray(data)
      ? ([...data] as unknown[])
      : { ...(data as Record<string, unknown>) };

    const sensitiveKeys = this.config.sensitiveKeys || [];

    if (!Array.isArray(sanitizedData)) {
      Object.keys(sanitizedData).forEach((key) => {
        // Check if this key should be redacted
        const shouldRedact = sensitiveKeys.some((sensitiveKey) =>
          key.toLowerCase().includes(sensitiveKey.toLowerCase())
        );

        if (shouldRedact) {
          sanitizedData[key] = "[REDACTED]";
        } else if (
          typeof sanitizedData[key] === "object" &&
          sanitizedData[key] !== null
        ) {
          // Recursively sanitize nested objects
          sanitizedData[key] = this.sanitizeData(sanitizedData[key]);
        }
      });
    }

    return sanitizedData;
  }

  /**
   * Send logs to a remote endpoint in a production environment
   */
  private async sendRemoteLogs(logEntry: LogEntry): Promise<void> {
    if (
      !this.config.enableRemoteLogging ||
      !this.config.remoteLoggingEndpoint
    ) {
      return;
    }

    // Only send error and warn logs to the remote endpoint
    if (logLevelPriority[logEntry.level] > logLevelPriority.warn) {
      return;
    }

    // Construct the full URL
    const endpointUrl = `${apiBaseUrl}${this.config.remoteLoggingEndpoint}`;

    // Retrieve the token from storage when sending logs
    const token = tokenStorage.getToken();

    // If no token, we probably shouldn't even try (unless endpoint is public, which it isn't)
    if (!token) {
      if (this.config.enableConsole) {
        console.warn("Cannot send remote logs: User not authenticated (no token).");
      }
      return;
    }

    try {
      // Use the fetch API to send logs to the full URL
      await fetch(endpointUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`, // Use the retrieved token
        },
        body: JSON.stringify(logEntry),
        // Don't wait for response if there are network issues
        signal: AbortSignal.timeout(3000),
      });
    } catch (e) {
      // Silently fail - we don't want remote logging failures to affect the app
      if (this.config.enableConsole) {
        console.error(`Failed to send logs to remote endpoint (${endpointUrl}):`, e);
      }
    }
  }

  /**
   * Internal method to handle logging at any level
   */
  private log(level: LogLevel, message: string, data?: unknown): void {
    // Check if this log level should be processed
    if (logLevelPriority[level] > logLevelPriority[this.config.minLevel]) {
      return;
    }

    // Sanitize any sensitive data
    const sanitizedData = this.sanitizeData(data) as LogData;

    // Format the log entry
    const timestamp = new Date().toISOString();
    const logEntry: LogEntry = {
      level,
      message,
      data: sanitizedData || {},
      timestamp,
      context: this.context || undefined,
    };

    // Add to history (with size limit)
    this.logHistory.push(logEntry);
    while (this.logHistory.length > (this.config.maxLogSize || 100)) {
      this.logHistory.shift();
    }

    // Log to console if enabled
    if (this.config.enableConsole) {
      const consoleMethod = level === "debug" ? "log" : level;
      const contextPrefix = this.context ? `[${this.context}] ` : "";
      if (typeof console[consoleMethod] === "function") {
        console[consoleMethod](
          `[${level.toUpperCase()}] ${contextPrefix}${message}`,
          sanitizedData || ""
        );
      } else {
        console.log(
          `[${level.toUpperCase()}] ${contextPrefix}${message}`,
          sanitizedData || ""
        );
      }
    }

    // Persist logs
    if (this.config.enablePersistence) {
      try {
        this.persistenceStrategy.saveLogs(this.logHistory);
      } catch (e) {
        // If there's an error storing logs, log to console as fallback
        if (this.config.enableConsole) {
          console.error("Failed to persist logs:", e);
        }
      }
    }

    // Send to remote logging in production if enabled
    if (this.config.enableRemoteLogging) {
      this.sendRemoteLogs(logEntry);
    }
  }

  /**
   * Log an error message
   */
  error(message: string, data?: unknown): void {
    this.log("error", message, data);
  }

  /**
   * Log a warning message
   */
  warn(message: string, data?: unknown): void {
    this.log("warn", message, data);
  }

  /**
   * Log an info message
   */
  info(message: string, data?: unknown): void {
    this.log("info", message, data);
  }

  /**
   * Log a debug message
   */
  debug(message: string, data?: unknown): void {
    this.log("debug", message, data);
  }

  /**
   * Get all logs currently in memory
   */
  getLogs(): LogEntry[] {
    return [...this.logHistory];
  }

  /**
   * Get logs filtered by level
   */
  getLogsByLevel(level: LogLevel): LogEntry[] {
    return this.logHistory.filter((log) => log.level === level);
  }

  /**
   * Get logs filtered by context
   */
  getLogsByContext(context: string): LogEntry[] {
    return this.logHistory.filter((log) => log.context === context);
  }

  /**
   * Clear both in-memory and persisted logs
   */
  clearLogs(): void {
    this.logHistory = [];
    this.persistenceStrategy.clearLogs();
  }

  /**
   * Update logger configuration
   */
  updateConfig(config: Partial<LoggerConfig>): void {
    this.config = {
      ...this.config,
      ...config,
    };
  }
}

// Export a singleton instance
export const logger = new Logger();
